import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import HtmlTemplateBuilder from './HtmlTemplateBuilder';
import './HtmlTemplateManager.css';

// Интерфейсы
interface HtmlTemplate {
  id: number;
  name: string;
  description: string;
  layout_type: string;
  status: string;
  containers_count: number;
  columns_count: number;
  error_count: number;
  warning_count: number;
  created_at: string;
  updated_at: string;
  created_by_name: string;
  is_active?: boolean; // Активен ли шаблон
  metadata?: { isSmokeTemplate?: boolean; isTestTemplate?: boolean }; // Smoke/test-шаблон
}

interface HtmlTemplateManagerProps {
  onSave?: (template: unknown) => void;
}

const HtmlTemplateManager: React.FC<HtmlTemplateManagerProps> = ({ onSave }) => {
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [templates, setTemplates] = useState<HtmlTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<'all' | 'regular' | 'smoke' | 'test'>('all'); // Фильтр типа

  // Загрузка списка шаблонов
  const loadTemplates = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get('/api/admin/v2/html-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      setError('Ошибка загрузки шаблонов');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка при монтировании
  useEffect(() => {
    loadTemplates();
  }, []);

  // Фильтрация шаблонов
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || template.status === filterStatus;
    
    // Фильтр по типу (обычные/smoke/test)
    const isSmoke = template.metadata?.isSmokeTemplate === true;
    const isTest = template.metadata?.isTestTemplate === true;
    const matchesType = filterType === 'all' 
      || (filterType === 'smoke' && isSmoke) 
      || (filterType === 'test' && isTest)
      || (filterType === 'regular' && !isSmoke && !isTest);
    
    return matchesSearch && matchesStatus && matchesType;
  });
  
  // Переключение активности шаблона
  const handleToggleActive = async (templateId: number, currentActive: boolean) => {
    try {
      await axios.patch(`/api/admin/v2/html-templates/${templateId}/toggle-active`, {
        is_active: !currentActive
      });
      await loadTemplates(); // Перезагружаем список
    } catch (error) {
      console.error('Error toggling template active state:', error);
      setError('Ошибка изменения статуса шаблона');
    }
  };

  // Обработчики
  const handleCreateNew = () => {
    setSelectedTemplateId(null);
    setView('create');
  };

  const handleEdit = (templateId: number) => {
    setSelectedTemplateId(templateId);
    setView('edit');
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот шаблон?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/v2/html-templates/${templateId}`);
      await loadTemplates(); // Перезагружаем список
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Ошибка удаления шаблона');
    }
  };

  const handleSave = (template: unknown) => {
    // Возвращаемся к списку после сохранения
    setView('list');
    loadTemplates(); // Перезагружаем список
    
    if (onSave) {
      onSave(template);
    }
  };

  const handleCancel = () => {
    setView('list');
  };

  // Получение иконки статуса
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '✅';
      case 'draft': return '📝';
      case 'archived': return '📦';
      default: return '❓';
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return '—';
    try {
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(new Date(value));
    } catch {
      return value;
    }
  };

  const renderStatusChip = (status: string) => {
    const label = {
      active: 'Опубликован',
      draft: 'Черновик',
      archived: 'Архив'
    }[status] || status;

    return (
      <span className={`template-status-chip template-status-chip--${status}`}>
        {getStatusIcon(status)} {label}
      </span>
    );
  };

  const renderQualityBadges = (template: HtmlTemplate) => {
    if (template.error_count === 0 && template.warning_count === 0) {
      return (
        <span className="quality-badge quality-badge--ok">
          ✅ Без ошибок
        </span>
      );
    }

    return (
      <div className="quality-badge__wrapper">
        {template.error_count > 0 && (
          <span className="quality-badge quality-badge--error">
            {template.error_count} критических
          </span>
        )}
        {template.warning_count > 0 && (
          <span className="quality-badge quality-badge--warning">
            {template.warning_count} предупреждений
          </span>
        )}
      </div>
    );
  };

  // Рендер списка шаблонов
  if (view === 'list') {
    return (
      <div className="html-template-manager">
        <div className="template-manager__header">
          <div className="template-manager__title">
            <h2>HTML Шаблоны</h2>
            <p>Управление шаблонами для генерации сайтов</p>
          </div>
          
          <button
            className="u2-btn is-primary"
            onClick={handleCreateNew}
            type="button"
          >
            Создать новый шаблон
          </button>
        </div>

        {/* Фильтры и поиск */}
        <div className="template-manager__filters">
          <div className="filter-group">
            <label className="u2-label">Поиск</label>
            <input
              type="text"
              placeholder="Название или описание..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="u2-input"
            />
          </div>
          
          <div className="filter-group">
            <label className="u2-label">Статус</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="u2-select"
            >
              <option value="all">Все</option>
              <option value="active">Активные</option>
              <option value="draft">Черновики</option>
              <option value="archived">Архивные</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="u2-label">Тип</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as 'all' | 'regular' | 'smoke' | 'test')}
              className="u2-select"
            >
              <option value="all">Все типы</option>
              <option value="regular">Обычные</option>
              <option value="smoke">Smoke-шаблоны</option>
              <option value="test">Test-шаблоны</option>
            </select>
          </div>
        </div>

        {/* Ошибки */}
        {error && (
          <div className="template-manager__error">
            ⚠️ {error}
          </div>
        )}

        {/* Загрузка */}
        {loading ? (
          <div className="template-manager__loading">
            🔄 Загрузка шаблонов...
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="template-manager__empty">
            {searchTerm || filterStatus !== 'all'
              ? '🔍 Шаблоны не найдены по заданным критериям'
              : '📝 Пока нет созданных шаблонов'}
          </div>
        ) : (
          <div className="u2-tableWrap" aria-label="HTML шаблоны">
            <table className="u2-table">
              <thead>
                <tr>
                  <th>Шаблон</th>
                  <th>Тип</th>
                  <th>Активен</th>
                  <th>Состав</th>
                  <th>Качество</th>
                  <th>Метаданные</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map(template => (
                  <tr key={template.id}>
                    <td>
                      <div className="template-name-cell">
                        {renderStatusChip(template.status)}
                        <div className="template-name-wrapper">
                          <div className="template-name-row">
                            <h3>{template.name}</h3>
                            <span className="template-layout-chip">
                              {template.layout_type}
                            </span>
                          </div>
                          {template.description && (
                            <p className="template-description">{template.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {template.metadata?.isTestTemplate ? (
                        <span className="template-type-badge template-type-badge--test">🧪 Test</span>
                      ) : template.metadata?.isSmokeTemplate ? (
                        <span className="template-type-badge template-type-badge--smoke">🧪 Smoke</span>
                      ) : (
                        <span className="template-type-badge template-type-badge--regular">📄 Обычный</span>
                      )}
                    </td>
                    <td>
                      <label className="template-active-toggle">
                        <input
                          type="checkbox"
                          checked={template.is_active !== false}
                          onChange={() => handleToggleActive(template.id, template.is_active !== false)}
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-label">
                          {template.is_active !== false ? 'Вкл' : 'Выкл'}
                        </span>
                      </label>
                    </td>
                    <td>
                      <div className="template-stat-row">
                        <span className="stat-label">Контейнеры</span>
                        <strong>{template.containers_count}</strong>
                      </div>
                      <div className="template-stat-row">
                        <span className="stat-label">Колонки</span>
                        <strong>{template.columns_count}</strong>
                      </div>
                    </td>
                    <td>{renderQualityBadges(template)}</td>
                    <td>
                      <div className="template-meta">
                        <span className="template-meta__item">
                          <strong>Автор:</strong> {template.created_by_name || '—'}
                        </span>
                        <span className="template-meta__item">
                          <strong>Создан:</strong> {formatDate(template.created_at)}
                        </span>
                        <span className="template-meta__item">
                          <strong>Обновлён:</strong> {formatDate(template.updated_at)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="template-actions">
                        <button
                          className="u2-btn is-secondary"
                          onClick={() => handleEdit(template.id)}
                          title="Редактировать"
                          type="button"
                        >
                          Редактировать
                        </button>
                        <button
                          className="u2-btn is-danger"
                          onClick={() => handleDelete(template.id)}
                          title="Удалить"
                          type="button"
                        >
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Рендер редактора/создания
  return (
    <div className="html-template-manager">
      <div className="template-manager__breadcrumb">
        <button
          className="u2-btn is-ghost"
          onClick={handleCancel}
          type="button"
        >
          ← Назад к списку шаблонов
        </button>
        <span className="breadcrumb-separator">/</span>
        <span>{view === 'create' ? 'Создание нового шаблона' : 'Редактирование шаблона'}</span>
      </div>
      
      <HtmlTemplateBuilder
        templateId={selectedTemplateId || undefined}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default HtmlTemplateManager;

